(() => {
  const sectionName = 'cart-drawer';
  let lastTrigger = null;
  let refreshController = null;

  const getRoot = () => document.querySelector('[data-cart-drawer]');
  const getPanel = () => getRoot()?.querySelector('[data-cart-drawer-panel]');
  const isOpen = () => getRoot()?.classList.contains('is-open') === true;
  const sectionUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionName);
    return url.toString();
  };
  const sectionsUrl = () => `${window.location.pathname}${window.location.search}`;

  function updateHeaderCount(root = getRoot()) {
    if (!root) return;
    const count = root.dataset.cartCount || '0';
    document.querySelectorAll('.cart-count').forEach((badge) => {
      badge.textContent = count;
    });
  }

  function setOpenState(open, focusPanel = true) {
    const root = getRoot();
    const panel = getPanel();
    if (!root || !panel) return;

    if (open && !document.body.classList.contains('cart-drawer-open')) {
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--cart-scrollbar-compensation', `${scrollbarWidth}px`);
    }

    root.classList.toggle('is-open', open);
    root.setAttribute('aria-hidden', String(!open));
    panel.inert = !open;
    document.body.classList.toggle('cart-drawer-open', open);
    if (!open) document.documentElement.style.removeProperty('--cart-scrollbar-compensation');
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(open));
    });

    if (open && focusPanel) window.setTimeout(() => panel.focus({ preventScroll: true }), 40);
  }

  function openDrawer(trigger = null) {
    if (trigger) lastTrigger = trigger;
    setOpenState(true);
  }

  function closeDrawer() {
    if (!isOpen()) return;
    setOpenState(false, false);
    const focusTarget = lastTrigger;
    lastTrigger = null;
    window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 330);
  }

  function parseSection(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-cart-drawer]');
  }

  function replaceSection(html, { open = false } = {}) {
    const currentRoot = getRoot();
    const nextRoot = parseSection(html);
    if (!currentRoot || !nextRoot) return false;

    const keepOpen = isOpen();
    if (keepOpen) {
      nextRoot.classList.add('is-open');
      nextRoot.setAttribute('aria-hidden', 'false');
      const nextPanel = nextRoot.querySelector('[data-cart-drawer-panel]');
      if (nextPanel) nextPanel.inert = false;
    }
    currentRoot.replaceWith(nextRoot);
    updateHeaderCount(nextRoot);

    if (open && !keepOpen) requestAnimationFrame(() => openDrawer(lastTrigger));
    return true;
  }

  async function refreshDrawer({ open = false, fallback = false } = {}) {
    refreshController?.abort();
    refreshController = new AbortController();
    try {
      const response = await fetch(sectionUrl(), {
        headers: { Accept: 'application/json' },
        signal: refreshController.signal,
      });
      if (!response.ok) throw new Error('Cart could not be refreshed.');
      const sections = await response.json();
      if (!replaceSection(sections[sectionName], { open })) throw new Error('Cart section was unavailable.');
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;
      if (fallback) window.location.assign(getRoot()?.dataset.cartUrl || '/cart');
      return false;
    }
  }

  function setDrawerBusy(item, busy) {
    const root = getRoot();
    root?.classList.toggle('is-updating', busy);
    item?.classList.toggle('is-updating', busy);
    item?.querySelectorAll('button, input, a').forEach((control) => {
      if ('disabled' in control) control.disabled = busy;
      control.setAttribute('aria-disabled', String(busy));
    });
  }

  function showDrawerError(message) {
    const status = getRoot()?.querySelector('[data-cart-drawer-status]');
    if (status) status.textContent = message;
  }

  async function changeLine(item, quantity) {
    const root = getRoot();
    if (!root || !item) return;
    setDrawerBusy(item, true);
    showDrawerError('');

    try {
      const response = await fetch(root.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: item.dataset.lineKey,
          quantity,
          sections: [sectionName],
          sections_url: sectionsUrl(),
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'This item could not be updated.');
      if (!replaceSection(payload.sections?.[sectionName])) throw new Error('The cart could not be redrawn.');
    } catch (error) {
      setDrawerBusy(item, false);
      showDrawerError(error.message || 'This item could not be updated. Please try again.');
    }
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !isOpen()) return;
    const panel = getPanel();
    if (!panel) return;
    const focusable = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-drawer-toggle]');
    if (toggle) {
      event.preventDefault();
      openDrawer(toggle);
      refreshDrawer();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      closeDrawer();
      return;
    }

    const item = event.target.closest('[data-cart-drawer-item]');
    if (!item) return;
    const input = item.querySelector('[data-cart-quantity-input]');
    const quantity = Math.max(0, Number(input?.value || 0));
    if (event.target.closest('[data-cart-quantity-minus]')) changeLine(item, Math.max(0, quantity - 1));
    if (event.target.closest('[data-cart-quantity-plus]')) changeLine(item, quantity + 1);
    if (event.target.closest('[data-cart-remove]')) {
      event.preventDefault();
      changeLine(item, 0);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;
    const item = input.closest('[data-cart-drawer-item]');
    const quantity = Math.max(0, Number(input.value || 0));
    input.value = quantity;
    changeLine(item, quantity);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen()) closeDrawer();
    trapFocus(event);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    const addButton = event.submitter?.closest('[data-add-to-cart]');
    if (!form || !addButton) return;
    event.preventDefault();

    const status = form.querySelector('[data-product-form-status]');
    const originalDisabled = addButton.disabled;
    addButton.disabled = true;
    addButton.classList.add('is-loading');
    if (status) status.textContent = 'Adding to your cart…';

    try {
      const formData = new FormData(form);
      formData.set('sections', sectionName);
      formData.set('sections_url', sectionsUrl());
      const response = await fetch(`${form.action}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'This item could not be added.');
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cartSectionHtml: payload.sections?.[sectionName], open: true, source: 'product' },
      }));
      if (status) status.textContent = 'Added to your cart.';
    } catch (error) {
      if (status) status.textContent = error.message || 'This item could not be added. Please try again.';
    } finally {
      addButton.classList.remove('is-loading');
      addButton.disabled = originalDisabled;
    }
  });

  document.addEventListener('cart:updated', (event) => {
    const detail = event.detail || {};
    if (detail.cartSectionHtml && replaceSection(detail.cartSectionHtml, { open: detail.open })) return;
    refreshDrawer({ open: detail.open, fallback: true });
  });

  updateHeaderCount();
})();
